import { useCallback } from 'react'
import { toast } from 'react-toastify'
import Popper from '@mui/material/Popper'
import Paper from '@mui/material/Paper'
import { ColorLensOutlined } from '@mui/icons-material'

import {
  IColorByValueConfig,
  useUpsertColorByValueConfigMutation
} from 'app/services/api/colorByValue'
import OverlayScrollbar from 'common/components/OverlayScrollBar'
import StyledToggle from 'common/components/StyledToggle'
import CBVThemeIcon from './CBVThemeIcon'

interface IColorByValueQuickToggleDropdown
  extends IColorByValueDropdownContent {
  anchorEl: Element | null
  setAnchorEl: (el: Element | null) => void
}

interface IColorByValueDropdownContent {
  config: IColorByValueConfig | undefined
  customizeColorsCB: () => void
  isCBVLoading: boolean
  setIsCBVLoading: (isCBVLoading: boolean) => void
}

const popperExtraTopMarginModifier = {
  name: 'offset',
  options: {
    offset: [0, 5]
  }
}

const DropdownContent = ({
  config,
  customizeColorsCB,
  isCBVLoading,
  setIsCBVLoading
}: IColorByValueDropdownContent) => {
  const [persistCBVConfig] = useUpsertColorByValueConfigMutation()

  const handleToggleChange = useCallback(
    (attrId: string, isChecked: boolean): void => {
      if (config == null) {
        return
      }

      setIsCBVLoading(true)

      persistCBVConfig({
        ...config,
        selectedAttributeId: isChecked ? attrId : null
      })
        .then(() => {
          setIsCBVLoading(false)
        })
        .catch(() => {
          toast.error('Color by Value update failed')
          setIsCBVLoading(false)
        })
    },
    [config, persistCBVConfig, setIsCBVLoading]
  )

  if (config == null) {
    return <div className='my-4 w-60'>Loading..</div>
  }

  if (config.attributesMap.length < 1) {
    return (
      <div className='my-4 w-60'>
        Cannot apply color to current set of attributes
      </div>
    )
  }

  return (
    <div
      className={`${
        config.loadStatus === 'init' || isCBVLoading ? 'opacity-30' : ''
      }`}
    >
      <div className='mb-4 max-w-md'>
        <div
          className={[
            'mb-2 grid grid-cols-6',
            'text-sm uppercase text-gray-500'
          ].join(' ')}
        >
          <div className='col-span-5 font-medium tracking-[0.03em] text-gray-500'>
            Attribute
          </div>
          <div className='col-span-1 font-medium tracking-[0.03em] text-gray-500'>
            Display
          </div>
        </div>
        <OverlayScrollbar>
          <div className='max-h-60'>
            {config.attributesMap.map((attr, idx) => {
              return (
                <div className='my-0.5 grid grid-cols-6 items-center' key={idx}>
                  <div className='col-span-5 flex items-center'>
                    <CBVThemeIcon
                      className='h-6 w-6 rounded border border-white'
                      themeName={attr.iconTheme}
                    />
                    <span className='mx-3'>{attr.attributeName}</span>
                  </div>
                  <StyledToggle
                    checked={attr.attributeId === config.selectedAttributeId}
                    onChange={(_evt, isChecked) => {
                      handleToggleChange(attr.attributeId, isChecked)
                    }}
                    disabled={isCBVLoading}
                    inputProps={{
                      'aria-label': `CBV dropdown attribute toggle ${idx}`
                    }}
                  />
                </div>
              )
            })}
          </div>
        </OverlayScrollbar>
      </div>
      <button
        className='flex items-center text-lighthouseBlue'
        onClick={customizeColorsCB}
      >
        <ColorLensOutlined />
        <div className='ml-2 font-medium'>Customize Colors</div>
      </button>
    </div>
  )
}

const Dropdown = ({
  anchorEl,
  config,
  customizeColorsCB,
  isCBVLoading,
  setIsCBVLoading
}: IColorByValueQuickToggleDropdown) => {
  return (
    <Popper
      open={anchorEl != null}
      anchorEl={anchorEl}
      style={{ zIndex: 1 }}
      placement='bottom-start'
      modifiers={[popperExtraTopMarginModifier]}
    >
      <Paper elevation={2}>
        <div className='p-6'>
          <DropdownContent
            config={config}
            customizeColorsCB={customizeColorsCB}
            isCBVLoading={isCBVLoading}
            setIsCBVLoading={setIsCBVLoading}
          />
        </div>
      </Paper>
    </Popper>
  )
}

export default Dropdown
